"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { useState, useEffect } from "react";
import type { AuthLoginResponse } from "@/lib/api/types/auth";
import { useAuth } from "@/lib/contexts/auth-context";
import { toast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function LoginPage() {
  const router = useRouter();
  const { login, logout } = useAuth();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorDesc, setErrorDesc] = useState("");

  // 로그인 페이지 진입 시 기존 인증 정보 제거
  useEffect(() => {
    logout().catch(() => {});
  }, []);

  return (
    <>
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex flex-col items-center justify-center mb-4 gap-2">
            <Image
              src="/logo.png"
              alt="ELPS Logo"
              width={240}
              height={136}
              className="rounded-lg"
              style={{ width: 'auto', height: 'auto' }}
            />
            <h2 className="text-xl font-semibold">Exmate ELPS</h2>
          </div>
        </div>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center text-white">로그인</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!userId || !password) {
                  setErrorTitle('입력 필요');
                  setErrorDesc('아이디와 패스워드를 입력하세요.');
                  setErrorOpen(true);
                  setTimeout(() => setErrorOpen(false), 2500);
                  return;
                }
                try {
                  setIsSubmitting(true);

                  // httpOnly 쿠키 설정을 위해 Next.js API Route를 경유합니다.
                  // Spring Boot를 직접 호출하면 Set-Cookie: httpOnly를 클라이언트가 제어할 수 없습니다.
                  const rawRes = await fetch('/api/auth/login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: userId, password }),
                  });

                  const res: AuthLoginResponse = await rawRes.json();

                  if (res?.success && res?.data) {
                    const { dpcCode, isSuperUser, adminNm, adminID, companyCode, companyName, userRole } = res.data;

                    // login()에는 토큰을 전달하지 않습니다 (httpOnly 쿠키로 이미 저장됨)
                    login({ dpcCode, adminID, isSuperUser, adminNm, companyCode, companyName, userRole });
                    router.push("/");
                  } else {
                    toast({
                      title: "로그인 실패",
                      description: res?.message || "아이디 또는 패스워드를 확인하세요.",
                      variant: "destructive",
                      hideClose: false,
                      duration: 3000,
                    });
                  }
                } catch (err) {
                  const e = err as { status?: number; message?: string } | Error;
                  if (typeof (e as any)?.status === 'number') {
                    const status = (e as any).status as number;
                    if (status === 400) {
                      setErrorTitle('로그인 실패');
                      setErrorDesc('등록되지 않은 사용자입니다.');
                      setErrorOpen(true);
                      setTimeout(() => setErrorOpen(false), 500);
                      return;
                    }
                    if (status === 401) {
                      setErrorTitle('로그인 실패');
                      setErrorDesc('비밀번호가 올바르지 않습니다.');
                      setErrorOpen(true);
                      setTimeout(() => setErrorOpen(false), 500);
                      return;
                    }
                  }
                  const msg = e instanceof Error ? e.message : '로그인 요청 중 오류가 발생했습니다.';
                  setErrorTitle('오류');
                  setErrorDesc(msg);
                  setErrorOpen(true);
                  setTimeout(() => setErrorOpen(false), 500);
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="userId" className="text-gray-200">아이디</Label>
                <Input
                  id="userId"
                  name="userId"
                  placeholder="아이디"
                  className="bg-gray-800 border-gray-700 text-gray-100 placeholder:text-gray-400"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-200">패스워드</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="패스워드"
                  className="bg-gray-800 border-gray-700 text-gray-100 placeholder:text-gray-400"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    id="remember"
                    type="checkbox"
                    className="h-4 w-4 text-blue-500 focus:ring-blue-400 border-gray-600 rounded bg-gray-800"
                  />
                  <Label htmlFor="remember" className="text-sm text-gray-300">
                    로그인 상태 유지
                  </Label>
                </div>
                <Link
                  href="/forgot-password"
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  비밀번호를 잊으셨나요?
                </Link>
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                로그인
              </Button>
            </form>

            <Separator />

            <div className="text-center">
              <p className="text-sm text-gray-300">
                계정이 없으신가요?{" "}
                <Link
                  href="/register"
                  className="font-medium text-blue-400 hover:text-blue-300"
                >
                  회원가입
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>

    <Dialog open={errorOpen} onOpenChange={setErrorOpen}>
      <DialogContent className="sm:max-w-[380px] [&>button]:hidden">
        <DialogHeader>
          <DialogTitle>{errorTitle}</DialogTitle>
          {errorDesc && <DialogDescription>{errorDesc}</DialogDescription>}
        </DialogHeader>
      </DialogContent>
    </Dialog>
    </>
  );
}
